import { searchBenefitEventResources } from '@bariendo/lib/benefit-events';
import { CoverageAction, PRIOR_AUTH_REQUIREMENT_CODESYSTEM } from '@bariendo/lib/constants';
import { readCoverageAction } from '@bariendo/lib/payment-status';
import { MedplumClient } from '@medplum/core';
import { Claim, ClaimResponse, Patient, Task } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { canSearchResourceType } from '@/services/permissions';

export interface UseBenefitEventsResult {
  resources: (Claim | ClaimResponse)[];
  /** The prior auth prep Tasks (BAR-3089), read only once a Benefit verified is recorded. */
  prepTasks: Task[];
  /** True until the first read for the current patient settles, so a filter can tell
   *  "nothing yet" from "nothing". */
  loading: boolean;
  /** Re-read after the drawer writes, and from the timeline's composite refresh. */
  refresh: () => Promise<void>;
}

/** No prep Tasks without a Benefit verified, nor for a role whose policy can't search Task. */
async function searchPrepTasks(
  medplum: MedplumClient,
  patientId: string,
  resources: (Claim | ClaimResponse)[]
): Promise<Task[]> {
  const verified = resources.some((resource) => readCoverageAction(resource) === CoverageAction.BENEFIT_VERIFIED);
  if (!verified || !canSearchResourceType(medplum, 'Task')) return [];
  try {
    return await medplum.searchResources(
      'Task',
      { subject: `Patient/${patientId}`, code: `${PRIOR_AUTH_REQUIREMENT_CODESYSTEM}|` },
      { cache: 'reload' }
    );
  } catch {
    return [];
  }
}

/**
 * Employer benefit events (BAR-2593) for one patient. A handful per patient, so a plain
 * search rather than the paginated GraphQL streams. Any failure — including a role whose
 * policy has no ClaimResponse read — degrades to no cards rather than an error.
 */
export function useBenefitEvents(patient: Patient | undefined): UseBenefitEventsResult {
  const medplum = useMedplum();
  const [resources, setResources] = useState<(Claim | ClaimResponse)[]>([]);
  const [prepTasks, setPrepTasks] = useState<Task[]>([]);
  const [settledFor, setSettledFor] = useState<string | undefined>();
  const requestIdRef = useRef(0);
  const patientId = patient?.id;

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    if (!patientId || !medplum.isAuthenticated()) {
      setResources([]);
      setPrepTasks([]);
      setSettledFor(undefined);
      return;
    }
    try {
      // The client caches GETs for 60s; a refresh after a write must not be served from it.
      const results = await searchBenefitEventResources(medplum, patientId, { cache: 'reload' });
      const tasks = await searchPrepTasks(medplum, patientId, results);
      if (requestId === requestIdRef.current) {
        setResources(results);
        setPrepTasks(tasks);
        setSettledFor(patientId);
      }
    } catch {
      if (requestId === requestIdRef.current) {
        setResources([]);
        setPrepTasks([]);
        setSettledFor(patientId);
      }
    }
  }, [medplum, patientId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { resources, prepTasks, loading: !!patientId && settledFor !== patientId, refresh };
}
